// Minimal reader for the KTX2 files assetconv writes (unsupercompressed BC1/BC2/BC3 with the
// original S3TC blocks) and a JS S3TC decoder with Mesa's rounding (the fallback when the GL has
// no WEBGL_compressed_texture_s3tc; same rules as crates/assetconv/src/image.rs decode_bc).

const VK = { 133: "BC1", 134: "BC1", 135: "BC2", 136: "BC2", 137: "BC3", 138: "BC3" };

export function parseKtx2(buf) {
  const id = new Uint8Array(buf, 0, 12);
  const want = [0xab, 0x4b, 0x54, 0x58, 0x20, 0x32, 0x30, 0xbb, 0x0d, 0x0a, 0x1a, 0x0a];
  if (!want.every((b, i) => id[i] === b)) throw new Error("not KTX2");
  const dv = new DataView(buf);
  const vk = dv.getUint32(12, true), w = dv.getUint32(20, true), h = dv.getUint32(24, true);
  const levelCount = Math.max(1, dv.getUint32(40, true)), scheme = dv.getUint32(44, true);
  if (scheme !== 0) throw new Error("supercompressed KTX2 not supported");
  const format = VK[vk];
  if (!format) throw new Error("vkFormat " + vk);
  const kvdOff = dv.getUint32(56, true), kvdLen = dv.getUint32(60, true);
  const kv = {};
  for (let o = kvdOff; o < kvdOff + kvdLen;) {
    const n = dv.getUint32(o, true);
    const bytes = new Uint8Array(buf, o + 4, n);
    const z = bytes.indexOf(0);
    kv[new TextDecoder().decode(bytes.subarray(0, z))] = new TextDecoder().decode(bytes.subarray(z + 1)).replace(/\0$/, "");
    o += 4 + n; o = (o + 3) & ~3;
  }
  const levels = [];
  for (let i = 0; i < levelCount; i++) {
    const off = Number(dv.getBigUint64(80 + 24 * i, true)), len = Number(dv.getBigUint64(88 + 24 * i, true));
    levels.push({ data: new Uint8Array(buf, off, len), width: Math.max(1, w >> i), height: Math.max(1, h >> i) });
  }
  return { format, width: w, height: h, levels, kv };
}

const e5r = c => ((c >> 8) & 0xf8) | ((c >> 13) & 0x7);
const e6g = c => ((c >> 3) & 0xfc) | ((c >> 9) & 0x3);
const e5b = c => ((c << 3) & 0xf8) | ((c >> 2) & 0x7);

/// S3TC level -> RGBA8 (rows in stored order).
export function decodeBC(format, w, h, data) {
  const bw = (w + 3) >> 2, bh = (h + 3) >> 2, bs = format === "BC1" ? 8 : 16;
  const out = new Uint8Array(w * h * 4);
  const px = new Uint8Array(64);
  for (let by = 0; by < bh; by++) for (let bx = 0; bx < bw; bx++) {
    const o = (by * bw + bx) * bs, co = format === "BC1" ? o : o + 8;
    const c0 = data[co] | (data[co + 1] << 8), c1 = data[co + 2] | (data[co + 3] << 8);
    const bits = (data[co + 4] | (data[co + 5] << 8) | (data[co + 6] << 16) | (data[co + 7] << 24)) >>> 0;
    const r0 = e5r(c0), g0 = e6g(c0), b0 = e5b(c0), r1 = e5r(c1), g1 = e6g(c1), b1 = e5b(c1);
    const four = format !== "BC1" || c0 > c1;
    for (let p = 0; p < 16; p++) {
      const code = (bits >>> (2 * p)) & 3;
      let r, g, b, a = 255;
      if (code === 0) { r = r0; g = g0; b = b0; }
      else if (code === 1) { r = r1; g = g1; b = b1; }
      else if (code === 2) {
        if (four) { r = ((r0 * 2 + r1) / 3) | 0; g = ((g0 * 2 + g1) / 3) | 0; b = ((b0 * 2 + b1) / 3) | 0; }
        else { r = (r0 + r1) >> 1; g = (g0 + g1) >> 1; b = (b0 + b1) >> 1; }
      } else if (four) { r = ((r0 + r1 * 2) / 3) | 0; g = ((g0 + g1 * 2) / 3) | 0; b = ((b0 + b1 * 2) / 3) | 0; }
      else { r = g = b = 0; a = format === "BC1" ? 0 : 255; }
      px[4 * p] = r; px[4 * p + 1] = g; px[4 * p + 2] = b; px[4 * p + 3] = a;
    }
    if (format === "BC2") {
      for (let p = 0; p < 16; p++) { const nib = (data[o + (p >> 1)] >> (4 * (p & 1))) & 15; px[4 * p + 3] = nib | (nib << 4); }
    } else if (format === "BC3") {
      const a0 = data[o], a1 = data[o + 1];
      let lo = (data[o + 2] | (data[o + 3] << 8) | (data[o + 4] << 16)) >>> 0, hi = (data[o + 5] | (data[o + 6] << 8) | (data[o + 7] << 16)) >>> 0;
      for (let p = 0; p < 16; p++) {
        const c = p < 8 ? (lo >>> (3 * p)) & 7 : (hi >>> (3 * (p - 8))) & 7;
        let a;
        if (c === 0) a = a0; else if (c === 1) a = a1;
        else if (a0 > a1) a = ((a0 * (8 - c) + a1 * (c - 1)) / 7) | 0;
        else if (c < 6) a = ((a0 * (6 - c) + a1 * (c - 1)) / 5) | 0;
        else a = c === 6 ? 0 : 255;
        px[4 * p + 3] = a;
      }
    }
    for (let j = 0; j < 4; j++) {
      const y = by * 4 + j; if (y >= h) break;
      for (let i = 0; i < 4; i++) {
        const x = bx * 4 + i; if (x >= w) break;
        out.set(px.subarray(4 * (4 * j + i), 4 * (4 * j + i) + 4), (y * w + x) * 4);
      }
    }
  }
  return out;
}

/// three.js texture from a KTX2 file: CompressedTexture when the GL can sample S3TC, else the
/// decoded level 0 as a DataTexture. Rows are uploaded in file order (flipY false).
export function ktx2Texture(THREE, renderer, buf) {
  const k = parseKtx2(buf);
  const s3tc = renderer.extensions.has("WEBGL_compressed_texture_s3tc");
  let tex;
  if (s3tc) {
    const fmt = { BC1: THREE.RGBA_S3TC_DXT1_Format, BC2: THREE.RGBA_S3TC_DXT3_Format, BC3: THREE.RGBA_S3TC_DXT5_Format }[k.format];
    // WebGL requires block-multiple sizes for every uploaded level; keep the levels that are
    tex = new THREE.CompressedTexture(k.levels.filter(l => l.width % 4 === 0 && l.height % 4 === 0), k.width, k.height, fmt);
    if (tex.mipmaps.length < 2) tex.minFilter = THREE.LinearFilter;
  } else {
    tex = new THREE.DataTexture(decodeBC(k.format, k.width, k.height, k.levels[0].data), k.width, k.height, THREE.RGBAFormat);
    tex.minFilter = THREE.LinearFilter;
  }
  tex.flipY = false;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return { texture: tex, info: { format: k.format, width: k.width, height: k.height, levels: k.levels.length, orientation: k.kv.KTXorientation, gpu_s3tc: s3tc } };
}
